/**
 * RFI Context
 * 
 * Uses the generic createTicketContext factory from the ticket system.
 * Provides centralized data access for RFI list and individual RFIs.
 * Also provides users list for "Assign To" dropdowns.
 * 
 * Uses: createTicketContext from features/tickets
 * 
 * @context RFIContext
 * 
 * @typedef {Object} RFIContextType
 * @property {Array} rfis - List of all RFIs (aliased from items)
 * @property {boolean} loading - Whether data is currently being loaded
 * @property {Function} refreshRFIs - Refresh the RFIs list from the API
 * @property {Function} getRFIById - Get an RFI by ID (from cache or API)
 * @property {Array} users - List of all active users
 * @property {boolean} usersLoading - Whether users data is currently being loaded
 */

import { useState, useEffect, useMemo } from "react";
import { createTicketContext } from "../../../features/tickets";
import { getRFIs, getRFIById } from "../../../services/api/rfi";
import { getUsersByRole } from "../../../services/api/core";

// Create context using the generic ticket factory
const {
  TicketContext,
  useTicketContext: useBaseTicketContext,
  useTicketCtx: useBaseTicketCtx,
} = createTicketContext({
  name: "RFI",
  fetchAll: getRFIs,
  fetchById: getRFIById,
  // No secondary data needed for RFI (unlike T&M which has pricing)
});

// Export the context
export const RFIContext = TicketContext;

// Create custom hooks that extend the base context with users
export function useRFIContext() {
  const context = useBaseTicketContext();
  if (!context) {
    throw new Error("useRFIContext must be used within a RFIProvider");
  }
  return context;
}

export function useRFICtx() {
  // Get base context value from factory
  const baseContext = useBaseTicketCtx();
  
  // Add users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Fetch all active users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const data = await getUsersByRole({ active: true });
        setUsers(data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Merge base context with users
  const contextValue = useMemo(
    () => ({
      ...baseContext,
      users,
      usersLoading,
    }),
    [baseContext, users, usersLoading]
  );

  return contextValue;
}

// Default export for backward compatibility pattern
export default useRFICtx;
